import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, ThumbsUp, Star, MessageSquare, Lightbulb, Send, Loader2, Lock, CheckCircle2, Trophy, Bell } from "lucide-react";

export const CollabPanel = ({ shareId, trip }) => {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [sug, setSug] = useState("");
  const [gc, setGc] = useState("");
  const [busy, setBusy] = useState(false);
  const [locking, setLocking] = useState(false);
  const seenMarked = useRef(false);

  const activities = [];
  (trip.itinerary || []).forEach((d, di) =>
    ((d.activities && d.activities.length ? d.activities : d.stops) || []).forEach((a, ai) =>
      activities.push({ key: `${di}-${ai}`, name: a.name, day: d.day })
    )
  );

  const load = () => {
    // Logged-in visitors auto-join (accurate participant count + consensus);
    // logged-out visitors get the read-only public view.
    const p = user
      ? api.post(`/collab/${shareId}/join`)
      : api.get(`/public/trips/${shareId}/collab`);
    return p.then((r) => setC(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [shareId, user]);

  // Owner: clear the "new activity" badge shortly after viewing.
  useEffect(() => {
    if (!c || !user || seenMarked.current) return;
    if (c.owner_id === user.id && (c.owner_unread || 0) > 0) {
      seenMarked.current = true;
      const t = setTimeout(() => {
        api.post(`/collab/${shareId}/seen`).then((r) => setC(r.data)).catch(() => {});
      }, 2500);
      return () => clearTimeout(t);
    }
  }, [c, user, shareId]);

  const needAuth = () => { toast.info("Sign in to join and collaborate."); };
  const call = async (fn) => {
    if (!user) return needAuth();
    if (locked) { toast.info("This plan is locked — voting is closed."); return; }
    setBusy(true);
    try { const r = await fn(); setC(r.data); } catch { toast.error("Something went wrong."); }
    finally { setBusy(false); }
  };

  const lockPlan = async () => {
    if (!window.confirm("Lock the plan? This freezes all votes and emails the final itinerary to everyone on the trip. This can't be undone.")) return;
    setLocking(true);
    try {
      const r = await api.post(`/collab/${shareId}/lock`);
      setC(r.data);
      toast.success("Plan locked — the final itinerary is on its way to everyone's inbox.");
    } catch {
      toast.error("Couldn't lock the plan. Please try again.");
    } finally { setLocking(false); }
  };

  if (!c) return null;
  const participants = c.participant_count || 1;
  const uid = user?.id;
  const fb = c.feedback || {};
  const locked = !!c.locked;
  const isOwner = uid && c.owner_id === uid;
  const unread = isOwner ? (c.owner_unread || 0) : 0;

  // Crowd favourites = the activities with the most votes (min 1 vote).
  const votesOf = (k) => (fb[k]?.votes?.length || 0);
  const maxVotes = activities.reduce((m, a) => Math.max(m, votesOf(a.key)), 0);
  const favouriteKeys = maxVotes > 0 ? activities.filter((a) => votesOf(a.key) === maxVotes).map((a) => a.key) : [];
  const favourites = activities.filter((a) => favouriteKeys.includes(a.key));

  return (
    <section data-testid="collab-panel" className="mt-14 rounded-3xl border border-black/5 bg-white/60 p-6 sm:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h2 className="font-display text-3xl flex items-center gap-2"><Users className="w-6 h-6 text-[#C8553D]" /> Plan together</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{participants} traveller{participants > 1 ? "s" : ""} on this trip</span>
          {unread > 0 && (
            <span data-testid="collab-unread-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#C8553D] text-[#FDFBF7] animate-pulse">
              <Bell className="w-3.5 h-3.5" /> {unread} new
            </span>
          )}
          {isOwner && !locked && (
            <button data-testid="collab-lock-btn" onClick={lockPlan} disabled={locking}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors disabled:opacity-60">
              {locking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Lock the plan
            </button>
          )}
        </div>
      </div>
      {locked && (
        <div data-testid="collab-locked-banner" className="mb-6 rounded-2xl bg-[#1A3B2B] text-[#FDFBF7] p-4 text-sm flex items-center gap-2.5">
          <CheckCircle2 className="w-5 h-5 text-[#C8553D] shrink-0" />
          <span>This plan is <strong>locked</strong>. Voting and suggestions are closed and the final itinerary was emailed to everyone on the trip.</span>
        </div>
      )}
      {!user && !locked && (
        <div className="mb-6 rounded-2xl bg-[#1A3B2B]/5 border border-[#1A3B2B]/15 p-4 text-sm">
          You can read everything here. <Link to="/auth" className="text-[#C8553D] underline">Sign in</Link> to vote, suggest and comment.
        </div>
      )}

      {/* Crowd favourites */}
      {favourites.length > 0 && (
        <div data-testid="crowd-favourites" className="mb-6 rounded-2xl border border-[#C8553D]/25 bg-[#C8553D]/5 p-5">
          <h3 className="font-display text-xl mb-1 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#C8553D]" /> Crowd favourites</h3>
          <p className="text-sm text-muted-foreground mb-3">The group's top-voted picks so far — {maxVotes} vote{maxVotes > 1 ? "s" : ""} each.</p>
          <div className="flex flex-wrap gap-2">
            {favourites.map((a) => (
              <span key={a.key} data-testid={`crowd-fav-${a.key}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C8553D] text-[#FDFBF7] text-sm">
                <Star className="w-3.5 h-3.5 fill-[#FDFBF7]" /> D{a.day} · {a.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Activity consensus */}
      <h3 className="font-display text-xl mb-3">Vote on activities</h3>
      <div className="space-y-2 mb-8">
        {activities.map((a) => {
          const f = fb[a.key] || { votes: [], must_do: [] };
          const pct = Math.min(100, Math.round((f.votes.length / participants) * 100));
          const voted = uid && f.votes.includes(uid);
          const must = uid && f.must_do.includes(uid);
          const isFav = favouriteKeys.includes(a.key);
          return (
            <div key={a.key} data-testid={`collab-activity-${a.key}`} className={`p-3 rounded-xl border bg-[#FDFBF7] ${isFav ? "border-[#C8553D]/40 ring-1 ring-[#C8553D]/20" : "border-black/5"}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium flex items-center gap-2">
                  D{a.day} · {a.name}
                  {isFav && (
                    <span data-testid={`crowd-fav-badge-${a.key}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C8553D]/15 text-[#C8553D] text-[10px] uppercase tracking-wide font-semibold">
                      <Trophy className="w-3 h-3" /> Crowd favourite
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`collab-vote-${a.key}`} onClick={() => call(() => api.post(`/collab/${shareId}/feedback`, { activity_key: a.key, field: "votes" }))} disabled={busy || locked}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${voted ? "bg-[#1A3B2B] text-[#FDFBF7] border-[#1A3B2B]" : "border-black/15 hover:bg-black/5"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <ThumbsUp className="w-3.5 h-3.5" /> {f.votes.length}
                  </button>
                  <button data-testid={`collab-must-${a.key}`} onClick={() => call(() => api.post(`/collab/${shareId}/feedback`, { activity_key: a.key, field: "must_do" }))} disabled={busy || locked}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${must ? "bg-[#C8553D] text-[#FDFBF7] border-[#C8553D]" : "border-black/15 hover:bg-black/5"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <Star className={`w-3.5 h-3.5 ${must ? "fill-[#FDFBF7]" : ""}`} /> {f.must_do.length}
                  </button>
                </div>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="h-full bg-[#1A3B2B]" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[11px] text-muted-foreground">{pct}% agree</span>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Suggestions */}
        <div>
          <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-[#C8553D]" /> Suggestions</h3>
          <div className="flex gap-2 mb-3">
            <input data-testid="collab-suggest-input" value={sug} onChange={(e) => setSug(e.target.value)} disabled={locked} placeholder={locked ? "Plan locked" : "Suggest an activity…"} className="flex-1 min-w-0 text-sm rounded-full border border-black/10 bg-white px-4 py-2 outline-none focus:border-[#1A3B2B] disabled:opacity-50 disabled:bg-black/5" />
            <button data-testid="collab-suggest-btn" disabled={busy || locked || !sug.trim()} onClick={() => { const t = sug; setSug(""); call(() => api.post(`/collab/${shareId}/suggest`, { text: t })); }} className="p-2 rounded-full bg-[#1A3B2B] text-[#FDFBF7] disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
          <div className="space-y-2">
            {(c.suggestions || []).map((s) => (
              <div key={s.id} className="p-3 rounded-xl border border-black/5 bg-[#FDFBF7] text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{s.name}</span>
                  <button data-testid={`collab-sugvote-${s.id}`} onClick={() => call(() => api.post(`/collab/${shareId}/suggest/${s.id}/vote`))} disabled={busy || locked}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${uid && s.votes.includes(uid) ? "bg-[#1A3B2B] text-[#FDFBF7] border-[#1A3B2B]" : "border-black/15"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}><ThumbsUp className="w-3 h-3" /> {s.votes.length}</button>
                </div>
                <p className="text-muted-foreground mt-1">{s.text}</p>
              </div>
            ))}
            {(!c.suggestions || !c.suggestions.length) && <p className="text-sm text-muted-foreground">No suggestions yet.</p>}
          </div>
        </div>

        {/* General comments */}
        <div>
          <h3 className="font-display text-xl mb-3 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#C8553D]" /> Discussion</h3>
          <div className="flex gap-2 mb-3">
            <input data-testid="collab-comment-input" value={gc} onChange={(e) => setGc(e.target.value)} disabled={locked} placeholder={locked ? "Plan locked" : "Leave a comment…"} className="flex-1 min-w-0 text-sm rounded-full border border-black/10 bg-white px-4 py-2 outline-none focus:border-[#1A3B2B] disabled:opacity-50 disabled:bg-black/5" />
            <button data-testid="collab-comment-btn" disabled={busy || locked || !gc.trim()} onClick={() => { const t = gc; setGc(""); call(() => api.post(`/collab/${shareId}/comment`, { text: t, target: "general" })); }} className="p-2 rounded-full bg-[#1A3B2B] text-[#FDFBF7] disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
          <div className="space-y-2">
            {(c.comments || []).filter((x) => (x.target || "general") === "general").map((cm) => (
              <div key={cm.id} className="p-3 rounded-xl border border-black/5 bg-[#FDFBF7] text-sm">
                <span className="font-medium">{cm.name}</span>
                <p className="text-muted-foreground mt-1">{cm.text}</p>
              </div>
            ))}
            {(!c.comments || !c.comments.length) && <p className="text-sm text-muted-foreground">No comments yet.</p>}
          </div>
        </div>
      </div>
    </section>
  );
};
